<script>
  import {
    authState,
    isUserAnonymous,
    initiateDiscordOAuth,
  } from '../../lib/stores/auth.svelte.js';
  import {
    getSharingSettings,
    setSharingForGroup,
    setWantAlertsPreference,
  } from '../../lib/services/groups-api.js';
  import { route, navigate } from '../../lib/stores/router.svelte.js';

  // Settings areas shown in the left-nav.
  const SECTIONS = [
    { id: 'sharing', label: 'Servers' },
    { id: 'alerts', label: 'Alerts' },
  ];

  // Active section comes from the URL (/settings/{section}); default to sharing.
  let activeSection = $derived(
    SECTIONS.some((s) => s.id === route.params.section) ? route.params.section : 'sharing'
  );

  /** @type {Array<{ groupId: string, name: string, iconUrl: string|null, role: string, shareBinders: boolean }>} */
  let groups = $state([]);
  let loading = $state(true);
  let error = $state('');
  // groupIds currently mid-save, so we can disable the toggle and avoid races.
  let saving = $state({});

  // Global Want Alert preference (ADR-0007). Default ON, mirroring the backend.
  let wantAlertsEnabled = $state(true);
  let savingAlerts = $state(false);
  // Whether this account has a linked Discord user. Want Alerts are Discord DMs,
  // so without a link the toggle has nothing to act on — gate the section on it.
  let discordLinked = $state(false);

  // Load settings once Firebase auth has settled, re-running whenever the auth
  // identity changes. On a hard refresh authState.user is null until the
  // session restores, so gating on authState.ready avoids reporting the user as
  // anonymous and hiding the server list.
  //
  // The load is inlined with an `active` guard rather than a run-once flag: the
  // effect re-runs when the signed-in user changes, and the guard both ignores a
  // superseded fetch's result (so a slow response can't overwrite newer state)
  // and clears the previous user's data, preventing a stale/cross-account leak.
  $effect(() => {
    if (!authState.ready) return;

    let active = true;

    (async () => {
      if (isUserAnonymous()) {
        if (active) {
          groups = [];
          loading = false;
        }
        return;
      }
      loading = true;
      error = '';
      try {
        const data = await getSharingSettings();
        if (!active) return;
        groups = data.groups;
        wantAlertsEnabled = data.wantAlertsEnabled ?? true;
        discordLinked = data.discordLinked ?? false;
      } catch (e) {
        console.error('[Settings] Failed to load sharing settings:', e);
        if (!active) return;
        error = 'Could not load your servers. Please try again.';
        groups = [];
      } finally {
        if (active) loading = false;
      }
    })();

    return () => {
      active = false;
    };
  });

  async function toggleSharing(group) {
    if (saving[group.groupId]) return;
    const next = !group.shareBinders;

    // Optimistic update — revert on failure.
    const prev = group.shareBinders;
    group.shareBinders = next;
    saving[group.groupId] = true;
    try {
      await setSharingForGroup(group.groupId, next);
    } catch (e) {
      console.error('[Settings] Failed to update sharing:', e);
      group.shareBinders = prev;
      error = 'Could not update sharing. Please try again.';
    } finally {
      saving[group.groupId] = false;
    }
  }

  async function toggleWantAlerts() {
    if (savingAlerts) return;
    const next = !wantAlertsEnabled;

    // Optimistic update — revert on failure.
    const prev = wantAlertsEnabled;
    wantAlertsEnabled = next;
    savingAlerts = true;
    try {
      await setWantAlertsPreference(next);
    } catch (e) {
      console.error('[Settings] Failed to update want alerts preference:', e);
      wantAlertsEnabled = prev;
      error = 'Could not update your alert preference. Please try again.';
    } finally {
      savingAlerts = false;
    }
  }

  function selectSection(id) {
    navigate('settings', id === 'sharing' ? {} : { section: id });
  }
</script>

<main class="settings-page">
  <div class="settings-shell">
    <nav class="settings-nav" aria-label="Settings sections">
      <h1 class="settings-title">Settings</h1>
      {#each SECTIONS as section (section.id)}
        <button
          class="settings-nav-item"
          class:active={activeSection === section.id}
          onclick={() => selectSection(section.id)}
        >
          {section.label}
        </button>
      {/each}
    </nav>

    <section class="settings-content">
      {#if activeSection === 'sharing'}
        <header class="content-header">
          <h2>Servers</h2>
          <p class="content-desc">
            Choose which servers can see your binders. Turning a server off hides all of your
            binders from its search and stops its update notifications. You stay a member and can
            still browse everyone else's binders.
          </p>
        </header>

        {#if !authState.ready || loading}
          <div class="settings-loading">Loading servers...</div>
        {:else if isUserAnonymous()}
          <div class="settings-gate">
            <p>Sign in with Discord to manage where your binders are shared.</p>
            <button class="btn-discord" onclick={() => initiateDiscordOAuth('/settings')}>
              Sign in with Discord
            </button>
          </div>
        {:else if groups.length === 0}
          <div class="settings-empty">
            You're not in any servers yet. Join one to start sharing your binders.
          </div>
        {:else}
          {#if error}
            <div class="settings-error" role="alert">{error}</div>
          {/if}
          <ul class="server-list">
            {#each groups as group (group.groupId)}
              <li class="server-row">
                <div class="server-info">
                  <div class="server-icon">
                    {#if group.iconUrl}
                      <img src={group.iconUrl} alt="" referrerpolicy="no-referrer" />
                    {:else}
                      {group.name.charAt(0).toUpperCase()}
                    {/if}
                  </div>
                  <div class="server-name">{group.name}</div>
                </div>
                <label class="toggle">
                  <input
                    type="checkbox"
                    role="switch"
                    checked={group.shareBinders}
                    disabled={saving[group.groupId]}
                    onchange={() => toggleSharing(group)}
                    aria-label={`Share my binders with ${group.name}`}
                  />
                  <span class="toggle-track"><span class="toggle-thumb"></span></span>
                  <span class="toggle-label">{group.shareBinders ? 'Sharing' : 'Off'}</span>
                </label>
              </li>
            {/each}
          </ul>
        {/if}
      {/if}

      {#if activeSection === 'alerts'}
        <header class="content-header">
          <h2>Alerts</h2>
          <p class="content-desc">
            Want Alerts are private DMs from the bot when a card you searched for but couldn't get
            shows up in one of your servers.
          </p>
        </header>

        {#if !authState.ready || loading}
          <div class="settings-loading">Loading...</div>
        {:else if isUserAnonymous()}
          <div class="settings-gate">
            <p>Sign in with Discord to manage your alert preferences.</p>
            <button class="btn-discord" onclick={() => initiateDiscordOAuth('/settings/alerts')}>
              Sign in with Discord
            </button>
          </div>
        {:else if !discordLinked}
          <div class="settings-gate">
            <p>Link your Discord account to receive Want Alert DMs.</p>
            <button class="btn-discord" onclick={() => initiateDiscordOAuth('/settings/alerts')}>
              Link Discord
            </button>
          </div>
        {:else}
          {#if error}
            <div class="settings-error" role="alert">{error}</div>
          {/if}
          <ul class="server-list">
            <li class="server-row">
              <div class="server-info">
                <div class="server-name">DM me when a card I searched for becomes available</div>
              </div>
              <label class="toggle">
                <input
                  type="checkbox"
                  role="switch"
                  checked={wantAlertsEnabled}
                  disabled={savingAlerts}
                  onchange={toggleWantAlerts}
                  aria-label="Receive Want Alert DMs"
                />
                <span class="toggle-track"><span class="toggle-thumb"></span></span>
                <span class="toggle-label">{wantAlertsEnabled ? 'On' : 'Off'}</span>
              </label>
            </li>
          </ul>
          <p class="content-desc alerts-helper">
            This applies across all your servers. To silence alerts from one server only, go dark in
            that server's sharing settings.
          </p>
        {/if}
      {/if}
    </section>
  </div>
</main>

<style>
  .settings-page {
    padding: var(--space-lg) var(--space-xl) 80px;
    max-width: 1100px;
    margin: 0 auto;
    width: 100%;
  }

  .settings-shell {
    display: flex;
    gap: var(--space-xl);
    align-items: flex-start;
  }

  .settings-nav {
    flex: 0 0 200px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    position: sticky;
    top: var(--space-lg);
  }

  .settings-title {
    font-family: var(--font-serif);
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--color-text);
    margin: 0 0 var(--space-md);
  }

  .settings-nav-item {
    all: unset;
    cursor: pointer;
    padding: 8px 12px;
    border-radius: var(--radius-md);
    font-size: 14px;
    color: var(--color-text-secondary);
    transition: background 0.15s;
  }

  .settings-nav-item:hover {
    background: var(--color-bg-tray);
  }

  .settings-nav-item.active {
    background: var(--color-accent-bg);
    color: var(--color-text);
    font-weight: 600;
  }

  .settings-content {
    flex: 1;
    min-width: 0;
  }

  .content-header {
    margin-bottom: var(--space-lg);
  }

  .content-header h2 {
    font-family: var(--font-serif);
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--color-text);
    margin: 0 0 var(--space-xs);
  }

  .content-desc {
    font-size: 14px;
    color: var(--color-text-tertiary);
    line-height: 1.5;
    margin: 0;
    max-width: 60ch;
  }

  .alerts-helper {
    margin-top: 12px;
  }

  .settings-loading,
  .settings-empty {
    color: var(--color-text-tertiary);
    padding: 32px 0;
  }

  .settings-error {
    background: #fef2f2;
    color: #991b1b;
    border-radius: var(--radius-md);
    padding: 10px 14px;
    font-size: 13px;
    margin-bottom: 12px;
  }

  .settings-gate {
    background: var(--color-bg-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
    padding: 32px 24px;
    text-align: center;
  }

  .settings-gate p {
    color: var(--color-text-tertiary);
    font-size: 14px;
    margin: 0 0 16px;
  }

  .btn-discord {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #5865f2;
    color: #fff;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
  }

  .btn-discord:hover {
    background: #4752c4;
  }

  .server-list {
    list-style: none;
    margin: 0;
    padding: 0;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
    overflow: hidden;
    background: var(--color-bg-surface);
  }

  .server-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 18px;
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .server-row:last-child {
    border-bottom: none;
  }

  .server-info {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .server-icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-bg-tray);
    color: var(--color-text-secondary);
    font-weight: 700;
    font-size: 15px;
    overflow: hidden;
  }

  .server-icon img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .server-name {
    font-size: 15px;
    font-weight: 600;
    color: var(--color-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .toggle {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    flex-shrink: 0;
  }

  .toggle input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  /* Keyboard focus indicator: the native checkbox is visually hidden, so
     surface focus on the track for keyboard users. */
  .toggle input:focus-visible + .toggle-track {
    box-shadow:
      0 0 0 2px var(--color-bg-surface),
      0 0 0 4px #16a34a;
  }

  .toggle-track {
    width: 40px;
    height: 22px;
    border-radius: 999px;
    background: var(--color-text-placeholder);
    position: relative;
    transition: background 0.15s;
    flex-shrink: 0;
  }

  .toggle-thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.15s;
  }

  .toggle input:checked + .toggle-track {
    background: #16a34a;
  }

  .toggle input:checked + .toggle-track .toggle-thumb {
    transform: translateX(18px);
  }

  .toggle input:disabled + .toggle-track {
    opacity: 0.5;
  }

  .toggle-label {
    font-size: 13px;
    color: var(--color-text-tertiary);
    min-width: 48px;
  }

  @media (max-width: 768px) {
    .settings-shell {
      flex-direction: column;
    }

    .settings-nav {
      position: static;
      flex-direction: row;
      flex-wrap: wrap;
      flex-basis: auto;
    }
  }
</style>
